import React, { useRef } from "react";

import cx from "classnames";
import isEmpty from "lodash.isempty";

import { coalesce, setStateValue } from "@kazoohr/helpers";

import { useDropdownMenuHandlers } from "../../helpers/dropdownMenuHandlers";
import { useUIDSeed } from "../../helpers/useUIDSeed";
import { Spacer } from "../Grid";
import { Icon, SIZES } from "../Icon";
import { Popover } from "../Popover";

import styles from "./Select.scss";
import { SelectOption } from "./SelectOption";

// eslint-disable-next-line max-lines-per-function
export function RawSelect<Values extends string | number = string>({
  ariaLabelledby,
  ariaDescribedby,
  compact,
  dataTestId,
  disabledLabel,
  disabled,
  error,
  id,
  options,
  emptyState,
  selectedOptionValue,
  onChange,
  defaultText,
  isOpen,
  setOpen,
  triggerClassName,
  menuClassName,
  renderPopoverInPortal,
}: {
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  compact?: boolean;
  dataTestId?: string;
  disabledLabel?: string;
  disabled?: boolean;
  error?: string;
  id?: string;
  options: {
    label?: string;
    value: Values;
    disabled?: boolean;
    labelDecorator?: React.ReactNode;
  }[];
  emptyState?: React.ReactNode;
  selectedOptionValue?: Values | null;
  onChange?: (option: {
    label?: string;
    value: Values;
    disabled?: boolean;
  }) => void;
  defaultText?: string;
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  triggerClassName?: string;
  menuClassName?: string;
  renderPopoverInPortal: boolean;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const generateUID = useUIDSeed(id, menuClassName);

  const openMenu = setStateValue(setOpen, true);
  const closeMenu = setStateValue(setOpen, false);

  const focusOnTrigger = () => {
    // istanbul ignore else - noops
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const dropdownMenuHandlers = useDropdownMenuHandlers({
    focusOnTrigger,
    options,
    openMenu,
    closeMenu,
    toggleMenu: () => {
      focusOnTrigger();
      setOpen(!isOpen);
    },
  });
  const {
    highlightedIndex,
    triggerClickHandler,
    triggerKeyDownHandler,
    menuKeyDownHandler,
    menuMouseEnterHandler,
  } = dropdownMenuHandlers;

  const onOptionSelect = (option: {
    label?: string;
    value: Values;
    disabled?: boolean;
  }) => {
    onChange?.(option);
    closeMenu();
    focusOnTrigger();
  };

  const triggerButtonClassName = cx(
    styles["select-trigger"],
    {
      [styles.compact]: compact,
      [styles.error]: error,
    },
    triggerClassName
  );
  const menuListClassName = cx(
    styles["select-menu"],
    {
      [styles.compact]: compact,
    },
    menuClassName
  );

  const selectTriggerId = id || generateUID("select-trigger");
  const selectTriggerLabelId = `inner-${selectTriggerId}-label`;
  const selectMenuId = id ? `select-menu-${id}` : generateUID("select-menu");
  const ariaLabelIds = [ariaLabelledby, selectTriggerLabelId]
    .filter((id) => id)
    .join(" ");

  const selectedOption = coalesce(
    options.find(({ value }) => selectedOptionValue === value),
    {}
  );

  const isSelected = !isEmpty(selectedOption);

  const parentComponent = (
    <button
      type="button"
      id={selectTriggerId}
      className={triggerButtonClassName}
      disabled={disabled}
      onClick={triggerClickHandler}
      ref={triggerRef}
      aria-haspopup="listbox"
      aria-controls={selectMenuId}
      aria-expanded={isOpen}
      aria-labelledby={ariaLabelIds}
      aria-describedby={ariaDescribedby}
      onKeyDown={triggerKeyDownHandler}
      data-testid={dataTestId}
    >
      <span
        id={selectTriggerLabelId}
        className={cx(styles["trigger-button-label"], {
          [styles["trigger-button-label-selected"]]: isSelected,
          [styles["trigger-button-label-with-decorator"]]:
            selectedOption.labelDecorator,
        })}
      >
        {selectedOption.labelDecorator && selectedOption.labelDecorator}
        {selectedOption.labelDecorator && (
          <Spacer size="small" orientation="horizontal" />
        )}
        {selectedOption.label || selectedOption.value || defaultText}
      </span>
      <Icon
        icon="caret_dn"
        size={SIZES.Medium}
        className={styles[`trigger-button-icon${isOpen ? "-opened" : ""}`]}
        color="var(--confetti-text-secondary)"
      />
    </button>
  );

  const optionsList = (
    <ul
      id={selectMenuId}
      className={menuListClassName}
      role="listbox"
      onKeyDown={menuKeyDownHandler}
      onMouseEnter={menuMouseEnterHandler}
    >
      {emptyState && isEmpty(options) ? (
        <li role="none" className={styles["select-empty-state"]}>
          {emptyState}
        </li>
      ) : (
        options.map((option, optionIndex) => (
          <li key={option.value} role="none">
            <SelectOption<Values>
              compact={compact}
              disabledLabel={disabledLabel}
              option={option}
              selected={selectedOptionValue === option.value}
              highlighted={highlightedIndex === optionIndex}
              onSelect={onOptionSelect}
            />
          </li>
        ))
      )}
    </ul>
  );

  return (
    <div role="presentation" className={styles.select}>
      <Popover
        isOpen={isOpen}
        onSetOpen={setOpen}
        parent={parentComponent}
        popoverComponent={optionsList}
        className={styles["popover"]}
        position="bottom"
        data-testid="meatBallMenu-popover"
        renderInPortal={renderPopoverInPortal}
        popperProps={{
          className: styles["popover-wrapper"],
          sameWidth: true,
        }}
      />
    </div>
  );
}
